// #564 Einsatz-Tracking (T2): Datentypen von portal_meine_einsaetze/portal_einsatz_detail und reine Anzeige-Regeln
// (Stufen, Filter, Zeitleiste, Kosten). Nur aus den Daten der Portal-Funktionen, nichts geschätzt.

export interface EinsatzKurz {
  id: string; objekt_id: string | null; ticket_nummer: string | null; titel: string; kategorie: string | null; ort: string | null;
  einheit: string | null; status: string; prioritaet: string | null; created_at: string; updated_at: string | null; fotos: number;
  /** T3: Anfrage aus dem Portal oder Einsatz; Zahl der Portal-Nachrichten */
  art?: 'einsatz' | 'anfrage'; nachrichten?: number;
  /** Anfrage: die Verwaltung hat im Portal geantwortet (kommt mit T6-SQL; fehlt es, zählt nur „erledigt“) */
  beantwortet?: boolean;
}

export interface EinsatzVerlauf { art: 'status' | 'hausmeister' | 'termin'; wert: string | null; zeit: string; ende?: string | null; ganztaegig?: boolean | null }

export interface EinsatzDetail {
  id: string; ticket_nummer: string | null; titel: string; status: string; prioritaet: string | null; ort: string | null;
  created_at: string; quelle: string | null; beschreibung: string | null; kategorie: string | null; einheit: string | null;
  firma: string | null; beauftragt_am: string | null; verlauf: EinsatzVerlauf[];
  fotos: { id: string; art: 'vorher' | 'nachher'; aufgenommen: string }[];
  minuten: number; stundensatz: number | null;
  material: { bezeichnung: string; menge: number | string | null; einheit: string | null; einzelpreis: number | string | null }[];
}

// ── Stufen und Filter ──
export type EinsatzStufe = 'gemeldet' | 'in_arbeit' | 'wartet_handwerker' | 'wartet_rueckmeldung' | 'erledigt';
export type EinsatzFilter = 'offen' | 'in_arbeit' | 'erledigt';

export function einsatzStufe(status: string): EinsatzStufe {
  const s = (status ?? '').toLowerCase();
  if (s === 'erledigt' || s === 'geschlossen') return 'erledigt';
  if (s === 'warten_auf_dienstleister') return 'wartet_handwerker';
  if (s === 'warten_auf_rueckmeldung') return 'wartet_rueckmeldung';
  if (s === 'in_bearbeitung') return 'in_arbeit';
  return 'gemeldet';
}

export const STUFE_TEXT: Record<EinsatzStufe, string> = {
  gemeldet: 'Gemeldet',
  in_arbeit: 'In Arbeit',
  wartet_handwerker: 'Wartet auf Handwerker',
  wartet_rueckmeldung: 'Wartet auf Rückmeldung',
  erledigt: 'Erledigt',
};

/** Status einer Anfrage (Frage an die Verwaltung): „Gesendet“ bis zur Antwort, danach „Beantwortet“. */
export function anfrageBeantwortet(a: { status: string; beantwortet?: boolean | null }): boolean {
  const s = (a.status ?? '').toLowerCase();
  return a.beantwortet === true || s === 'erledigt' || s === 'geschlossen';
}
export const anfrageStatusText = (a: { status: string; beantwortet?: boolean | null }): string =>
  anfrageBeantwortet(a) ? 'Beantwortet' : 'Gesendet';

export function einsatzFilter(status: string): EinsatzFilter {
  const st = einsatzStufe(status);
  if (st === 'erledigt') return 'erledigt';
  return st === 'gemeldet' ? 'offen' : 'in_arbeit';
}

export const einsaetzeFuer = (alle: readonly EinsatzKurz[], objektId: string, f: EinsatzFilter): EinsatzKurz[] =>
  alle.filter((e) => e.objekt_id === objektId && einsatzFilter(e.status) === f);

export const anzahlJeFilter = (alle: readonly EinsatzKurz[], objektId: string): Record<EinsatzFilter, number> => ({
  offen: einsaetzeFuer(alle, objektId, 'offen').length,
  in_arbeit: einsaetzeFuer(alle, objektId, 'in_arbeit').length,
  erledigt: einsaetzeFuer(alle, objektId, 'erledigt').length,
});

// ── Zeitleiste ──
export interface ZeitleistenPunkt { text: string; zeit: string; zukunft: boolean }

/** „20.10. 08:12“ */
export const zeitText = (iso: string): string => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
};

function terminText(v: EinsatzVerlauf): string {
  const d = new Date(v.zeit);
  const tag = d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  if (v.ganztaegig) return `Termin am ${tag} (ganztägig)`;
  const von = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const bis = v.ende ? new Date(v.ende).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : null;
  return bis ? `Termin am ${tag}, ${von}–${bis} Uhr` : `Termin am ${tag}, ${von} Uhr`;
}

const STATUS_PUNKT: Record<string, string> = {
  in_bearbeitung: 'In Arbeit',
  warten_auf_dienstleister: 'Wartet auf Handwerker',
  warten_auf_rueckmeldung: 'Wartet auf Rückmeldung',
  erledigt: 'Erledigt',
  geschlossen: 'Erledigt',
};

/** Zeitleiste aus echten Einträgen: Meldung, Statuswechsel, Hausmeister, Handwerker, Termine (künftige hohl). */
export function zeitleiste(d: EinsatzDetail, jetzt: Date = new Date()): ZeitleistenPunkt[] {
  const meldung = d.quelle === 'mieterportal' ? 'Meldung durch den Mieter'
    : d.quelle === 'eigentuemerportal' ? 'Ihre Meldung' : 'Aufgenommen von der Verwaltung';
  const punkte: { text: string; zeit: string }[] = [{ text: meldung, zeit: d.created_at }];
  if (d.beauftragt_am && d.firma) punkte.push({ text: `Handwerker beauftragt: ${d.firma}`, zeit: d.beauftragt_am });
  for (const v of d.verlauf) {
    if (v.art === 'hausmeister' && v.wert) punkte.push({ text: `Hausmeister beauftragt: ${v.wert}`, zeit: v.zeit });
    else if (v.art === 'termin') punkte.push({ text: terminText(v), zeit: v.zeit });
    else if (v.art === 'status' && v.wert && STATUS_PUNKT[v.wert]) punkte.push({ text: STATUS_PUNKT[v.wert], zeit: v.zeit });
  }
  // gleiche aufeinanderfolgende Texte zusammenfassen (z. B. erledigt → geschlossen)
  const sortiert = punkte.sort((a, b) => a.zeit.localeCompare(b.zeit));
  return sortiert
    .filter((p, i) => i === 0 || p.text !== sortiert[i - 1].text)
    .map((p) => ({ ...p, zukunft: new Date(p.zeit).getTime() > jetzt.getTime() }));
}

// ── Kosten ──
export interface KostenZeile { text: string; betrag: number }
const r2 = (n: number) => Math.round(n * 100) / 100;
const euro = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const zahl = (v: unknown) => Number(v) || 0;

/** Arbeitszeit × Stundensatz und Material; leer, wenn nichts erfasst ist. */
export function kosten(d: Pick<EinsatzDetail, 'minuten' | 'stundensatz' | 'material'>): { zeilen: KostenZeile[]; summe: number } {
  const zeilen: KostenZeile[] = [];
  const satz = zahl(d.stundensatz);
  if (d.minuten > 0 && satz > 0) {
    const std = d.minuten / 60;
    zeilen.push({ text: `Arbeitszeit ${std.toLocaleString('de-DE', { maximumFractionDigits: 2 })} Std × ${euro(satz)}`, betrag: r2(std * satz) });
  }
  for (const m of d.material) {
    const betrag = r2(zahl(m.menge) * zahl(m.einzelpreis));
    if (betrag <= 0) continue;
    const menge = zahl(m.menge);
    zeilen.push({ text: `Material: ${m.bezeichnung}${menge && menge !== 1 ? ` (${menge.toLocaleString('de-DE')} ${m.einheit ?? 'Stk'})` : ''}`, betrag });
  }
  return { zeilen, summe: r2(zeilen.reduce((s, z) => s + z.betrag, 0)) };
}

/** T6 (Björn 26.10.): im Portal einheitlich brutto – Stundensatz und Material sind netto erfasst, Aufschlag 19 % USt. */
export const MWST_SATZ = 0.19;
export function kostenBrutto(d: Pick<EinsatzDetail, 'minuten' | 'stundensatz' | 'material'>): { zeilen: KostenZeile[]; summe: number; netto: number } {
  const f = 1 + MWST_SATZ;
  const b = kosten({
    minuten: d.minuten,
    stundensatz: d.stundensatz == null ? null : r2(zahl(d.stundensatz) * f),
    material: d.material.map((m) => ({ ...m, einzelpreis: r2(zahl(m.einzelpreis) * f) })),
  });
  return { ...b, netto: kosten(d).summe };
}

// ── T4 Kostenfreigabe ──
export interface KostenStand { stand: 'wartet' | 'freigegeben' | 'abgelehnt'; betrag: number | string | null; am?: string | null; begruendung?: string | null }

/** „1.234,50“ / „1234.5“ → Zahl; leer → null; ungültig → undefined (Büro-Eingabe der geschätzten Kosten) */
export function leseBetrag(text: string): number | null | undefined {
  const t = text.trim().replace(/\s|€/g, '');
  if (!t) return null;
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : undefined;
}

/** Zähler am Tab: offene Vorgänge plus solche, die auf eine Freigabe warten (ohne Doppelte). */
export function tabZaehler(alle: readonly EinsatzKurz[], objektId: string, staende: Readonly<Record<string, KostenStand | null>>): number {
  return alle.filter((e) => e.objekt_id === objektId && (einsatzFilter(e.status) === 'offen' || staende[e.id]?.stand === 'wartet')).length;
}
